import {ChangeDetectionStrategy, Component, EventEmitter, HostListener, Input, OnInit, Output} from "@angular/core";
import {DatePipe} from "@angular/common";
import {MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter} from "@angular/material-moment-adapter";
import {DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE} from "@angular/material/core";
import {MatIconModule} from "@angular/material/icon";
import {MatButtonModule} from "@angular/material/button";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {MatDatepicker, MatDatepickerInput} from "@angular/material/datepicker";
import {MatInput} from "@angular/material/input";
import {DateUtils} from "@app/utils/date-utils";
import {Moment} from "moment";

export const MY_MOMENT_FORMATS = {
    parse: {
        dateInput: 'MM/YYYY',
    },
    display: {
        dateInput: 'MM/YYYY',
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'MMMM YYYY',
    },
};

@Component({
    selector: 'spurado-date-navigator',
    templateUrl: './date-navigator.component.html',
    styleUrls: ['./date-navigator.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        DatePipe,
        {provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: {useUtc: true}},
        {
            provide: DateAdapter,
            useClass: MomentDateAdapter,
            deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
        },
        {provide: MAT_DATE_FORMATS, useValue: MY_MOMENT_FORMATS}
    ],
    standalone: true,
    imports: [MatIconModule, MatButtonModule, TranslateModule, MatDatepicker, MatDatepickerInput, MatInput]
})
export class DateNavigatorComponent implements OnInit {

    @Input() mode: 'week' | 'month' = 'week';
    @Input() firstDayOfPeriod: Date; // Start of week or start of month

    @Input() disabled: boolean = false;
    @Input() isLoading: boolean = false;
    @Input() displayWeekend: boolean;

    @Output() navigatePrevious = new EventEmitter<void>();
    @Output() navigateNext = new EventEmitter<void>();
    @Output() resetToCurrent = new EventEmitter<void>();
    @Output() dateSelected = new EventEmitter<Date>();

    protected pickerStartView: "month" | "year" | "multi-year" = "month";

    private dateUtils = new DateUtils();

    constructor(private datePipe: DatePipe, private translate: TranslateService) {
    }

    ngOnInit() {
        this.pickerStartView = this.mode == "week" ? "month" : "year";
    }

    @HostListener('document:keyup.ArrowLeft', ['$event.target'])
    public previous(element: HTMLElement): void {
        if (element.localName === 'body' && element.classList.contains('mat-body') && element.classList.contains('mat-app-background')) {
            this.navigatePrevious.emit();
        }
    }

    @HostListener('document:keyup.ArrowRight', ['$event.target'])
    public next(element: HTMLElement): void {
        if (element.localName === 'body' && element.classList.contains('mat-body') && element.classList.contains('mat-app-background')) {
            this.navigateNext.emit();
        }
    }

    onDateChange(event: any) {
        this.dateSelected.emit(event.value.toDate());
    }

    get label(): string {
        if (!this.firstDayOfPeriod) {
            return ''; //Component is not yet initialized ?!
        }

        if (this.mode === 'week') {
            return this.buildWeekLabel(this.firstDayOfPeriod);
        } else if (this.mode == 'month') {
            return this.datePipe.transform(this.firstDayOfPeriod, 'MMMM yyyy');
        } else {
            throw new Error(`Unsupported mode: ${this.mode}`);
        }
    }

    buildWeekLabel(firstDayOfWeek: Date): string {
        const lastDay = this.lastDayOfWeek(firstDayOfWeek);

        const firstDayNumber = firstDayOfWeek.getDate();
        const lastDayNumber = lastDay.getDate();

        const firstMonth = this.datePipe.transform(firstDayOfWeek, 'MMMM');
        const lastMonth = this.datePipe.transform(lastDay, 'MMMM');
        const year = lastDay.getFullYear();

        if (firstMonth === lastMonth) {
            return this.translate.instant('date-navigator.week_label', {
                from: firstDayNumber,
                to: lastDayNumber,
                monthYear: this.datePipe.transform(firstDayOfWeek, 'MMMM yyyy')
            });
        } else {
            return this.translate.instant('date-navigator.week_label_cross_month', {
                from: firstDayNumber,
                fromMonth: firstMonth,
                to: lastDayNumber,
                toMonth: lastMonth,
                year: year
            });
        }
    }

    lastDayOfWeek(firstDayOfWeek: Date): Date {
        const base = new Date(firstDayOfWeek); // do not mutate the original input
        const year = base.getFullYear();
        const month = base.getMonth();

        // Compute the last day of the current month
        const lastDayOfMonth = new Date(year, month + 1, 0);

        // JavaScript day of week (0 = Sunday, 6 = Saturday)
        const dayOfWeek = base.getDay();

        // ISO day of week (1 = Monday, 7 = Sunday)
        const isoDay = dayOfWeek === 0 ? 7 : dayOfWeek;

        let daysToAdd: number;

        if (this.displayWeekend) {
            // Week ends on Sunday
            // Number of days to add to reach Sunday of the same week
            daysToAdd = 7 - isoDay; // value between 0 and 6
        } else {
            // Week ends on Friday (business week)
            const businessWeekEnd = 5; // Friday in ISO format (1 = Monday, 5 = Friday)

            // If the date is between Monday and Friday, move forward to Friday.
            // If it is Saturday or Sunday, we keep the given date as the effective end
            // of the business week (daysToAdd = 0).
            daysToAdd = isoDay <= businessWeekEnd ? businessWeekEnd - isoDay : 0;
        }

        // Compute the end-of-week date according to the above rules
        const endOfWeek = new Date(base);
        endOfWeek.setDate(endOfWeek.getDate() + daysToAdd);

        // If the computed end-of-week day is still within the same month, return it
        if (endOfWeek.getMonth() === month) {
            return endOfWeek;
        }

        // Otherwise, return the last day of the current month
        return lastDayOfMonth;
    }

    get isCurrentDate(): boolean {
        if (!this.firstDayOfPeriod) {
            return false;
        }

        const today = new Date();

        if (this.mode === 'week') {
            const currentWeek = this.dateUtils.getWeekNumber(today);
            const displayedWeek = this.dateUtils.getWeekNumber(this.firstDayOfPeriod);
            const sameYear = today.getFullYear() === this.firstDayOfPeriod.getFullYear();

            return currentWeek === displayedWeek && sameYear;
        }

        if (this.mode === 'month') {
            return today.getMonth() === this.firstDayOfPeriod.getMonth() && today.getFullYear() === this.firstDayOfPeriod.getFullYear();
        }

        return false;
    }

    onMonthSelected(event: Moment, datepicker: MatDatepicker<Date>) {
        if (this.mode === 'week') {
            return;
        }

        datepicker.close();

        const selectedMonth = new Date(event.toDate().getFullYear(), event.toDate().getMonth(), 1);
        this.dateSelected.emit(selectedMonth);
    }

}
